/**
 * Desktop notifications for calls.
 *
 * A browser only lets a tab bring itself to the front from a click on
 * something that tab owns. A notification created by the call tab is such a
 * thing, so clicking it is the reliable way back to the call tab; a link in
 * another tab calling window.focus() is ignored.
 *
 * Notification text stays generic: it can show on a lock screen, so it never
 * includes the caller's number or name.
 *
 * Behind the desktop-call-notifications flag: with it off, nothing is shown
 * and no permission is requested.
 */
import { POSTHOG_FLAGS } from '@/constants/posthogFlags'
import { isPostHogFlagEnabled } from '@/utils/posthogFlags'

export function callNotificationsEnabled() {
  return isPostHogFlagEnabled(POSTHOG_FLAGS.DESKTOP_CALL_NOTIFICATIONS)
}

/** @returns {'unsupported' | 'default' | 'granted' | 'denied'} */
export function notificationPermission() {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported'
  return window.Notification.permission
}

/**
 * Must be called from a click: browsers ignore permission requests otherwise.
 * @returns {Promise<'unsupported' | 'default' | 'granted' | 'denied'>}
 */
export async function requestNotificationPermission() {
  if (notificationPermission() !== 'default') return notificationPermission()
  try {
    return await window.Notification.requestPermission()
  } catch {
    return notificationPermission()
  }
}

/**
 * Shows a notification that brings this tab to the front when clicked.
 * Tabs that use the same tag replace each other's notification, so several
 * ringing tabs show one notification.
 * @param {{ title: string, body: string, tag: string }} options
 * @returns {Notification | null}
 */
export function showCallNotification({ title, body, tag }) {
  if (!callNotificationsEnabled() || notificationPermission() !== 'granted') return null
  try {
    const notification = new window.Notification(title, { body, tag })
    notification.onclick = () => {
      window.focus()
      notification.close()
    }
    return notification
  } catch (e) {
    console.warn('[callNotifications] could not show notification:', e)
    return null
  }
}

/**
 * Picks which troubleshooting steps to show. Edge's user agent also says
 * "Chrome", so check for Edge first.
 * @returns {{ browser: 'edge' | 'chrome' | 'other', os: 'mac' | 'windows' | 'other' }}
 */
export function detectBrowserAndOs() {
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent
  let browser = 'other'
  if (/Edg\//.test(ua)) browser = 'edge'
  else if (/Chrome\//.test(ua)) browser = 'chrome'
  let os = 'other'
  if (/Mac OS X|Macintosh/.test(ua)) os = 'mac'
  else if (/Windows/.test(ua)) os = 'windows'
  return { browser, os }
}
